import { FC, useCallback, useEffect, useMemo, useState } from "react"
import { useSelector } from "react-redux"
import ContactAutocomplete from "./ContactAutocomplete"
import RecipientChips from "./RecipientChips"
import { Contact, getRecipientNames, MessageType } from "./utils"
import Toggle from "../shared/Toggle"
import AlertSetting from "@/components/AlertSetting"
import Actions from "@/components/AlertSetting/Actions"
import { IconColor, IconProps } from "@/components/shared/Icon"
import Spinner, { SpinnerSize } from "@/components/shared/Spinner"
import useFormStateHandler from "@/hooks/useFormStateHandler"
import { RootState } from "@/store"
import "./index.scss"

export interface AlertMessageForm {
   activated: boolean
   recipients: string[]
}

interface AlertMessageSettingsProps {
   form: AlertMessageForm
   contacts: Contact[]
   onSave: (form: AlertMessageForm) => void
   messageType: MessageType
   icon: IconProps["name"]
   title: string
}

const AlertMessageSettings: FC<AlertMessageSettingsProps> = ({
   form,
   contacts,
   onSave,
   messageType,
   icon,
   title,
}) => {
   const usersLoading = useSelector(
      ({ usersReducer }: RootState) => usersReducer.loading
   )

   const [isEditing, setIsEditing] = useState(false)
   const [formState, setState] = useFormStateHandler<AlertMessageForm>(form)

   useEffect(() => {
      setState({ ...form })
   }, [form, setState])

   const isFormValid = useMemo(() => {
      return !formState.activated || formState.recipients.length !== 0
   }, [formState.activated, formState.recipients.length])

   const isFormChanged = useMemo(() => {
      return (
         form.activated !== formState.activated ||
         form.recipients.join() !== formState.recipients.join()
      )
   }, [form, formState])

   const recipientNames = useMemo(
      () => getRecipientNames(formState.recipients, contacts),
      [formState.recipients, contacts]
   )

   const handleCancel = useCallback(() => {
      setState({ ...form })
      setIsEditing(false)
   }, [form, setState])

   const handleSave = useCallback(() => {
      onSave(formState)
      setIsEditing(false)
   }, [onSave, formState])

   const handleDelete = useCallback(() => {
      setState({ activated: false, recipients: [] })
   }, [setState])

   const renderContent = () => {
      if (usersLoading) {
         return <Spinner size={SpinnerSize.SMALL} />
      }

      if (isEditing) {
         return (
            <ContactAutocomplete
               contacts={contacts}
               messageType={messageType}
               selectedRecipients={formState.recipients}
               onSelectionChange={(recipients) =>
                  setState({ ...formState, recipients })
               }
            />
         )
      }

      if (formState.recipients.length === 0) {
         return (
            <div className="alert-message-setting__no-content">
               No recipients have been added to this alarm notification
            </div>
         )
      }

      return (
         <div>
            <div className="alert-message-setting__label">Send to:</div>
            <RecipientChips items={recipientNames} />
         </div>
      )
   }

   return (
      <AlertSetting
         iconColor={IconColor.HONOLULU_BLUE}
         iconName={icon}
         title={title}
      >
         <div className="alert-message-setting">
            <div className="alert-message-setting__title">
               <Toggle
                  className="alert-message-setting__toggle"
                  label={`Send ${messageType}`}
                  value={formState.activated}
                  disabled={!isEditing}
                  onChange={(checked) =>
                     setState({ ...formState, activated: checked })
                  }
               />
               <Actions
                  isDisabled={!isFormChanged || !isFormValid}
                  isEditing={isEditing}
                  hasDelete
                  onCancel={handleCancel}
                  onEdit={() => setIsEditing(true)}
                  onSave={handleSave}
                  onDelete={handleDelete}
               />
            </div>
            {renderContent()}
         </div>
      </AlertSetting>
   )
}

export default AlertMessageSettings
